import { Divider, Typography } from '@material-ui/core';
import { ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { AnchorButton, Box, Button, TextBox } from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import { useMemo } from 'react';
import { EmptySearchLoadsIcon } from 'shared/icons/EmptySearchLoadsIcon';
import { ExploreSuggestedLoads } from 'shared/modules/load-offers/ExploreSuggestedLoads';
import { useLoadSuggestions } from 'shared/modules/load-suggestions/data/LoadSuggestionsAPI';
import styled from 'styled-components';
import { formatLoadStage } from '../data/LoadDTO';
import { useLoadsPageContext } from './data/LoadsPageParamsContext';

const StyledText = styled.div`
  overflow: hidden;
  text-align: center;
  color: ${ColorDynamic.Dark300};

  &:before,
  &:after {
    background-color: ${ColorDynamic.Silver500};
    content: '';
    display: inline-block;
    height: 1px;
    position: relative;
    vertical-align: middle;
    width: 100px;
  }
  &:before {
    right: 1em;
    margin-left: -50%;
  }
  &:after {
    left: 1em;
    margin-right: -50%;
  }
`;

const StyledDivider = styled(Divider)`
  width: 102px;
`;

function LoadsPageEmptyResultSearch() {
  const { updateParams } = useLoadsPageContext();

  return (
    <Stack space="small" align="center">
      <Stack space="medium" align="center">
        <EmptySearchLoadsIcon />

        <Stack align="center">
          <Typography variant="h2">Couldn&apos;t Find Anything</Typography>
          <Stack space="none">
            <TextBox color="secondary" align="center">
              Try modifying your search request.
            </TextBox>
          </Stack>
        </Stack>
      </Stack>
      <Button
        variant="neutral"
        onClick={() => {
          updateParams((prev) => ({ stage: prev.stage }), {
            strategy: 'reset',
          });
        }}
      >
        Go Back to Loads
      </Button>
    </Stack>
  );
}

function LoadsPageEmptyResultFallback() {
  const { params } = useLoadsPageContext();

  return (
    <Stack space="small" align="center">
      <Stack space="medium" align="center">
        <EmptySearchLoadsIcon />

        <Typography variant="h2">
          No {formatLoadStage(params.stage)} loads
        </Typography>
      </Stack>
      <AnchorButton
        href={parseURITemplate(
          '/loadboard{?utm_source,utm_medium,utm_content}',
          {
            utm_source: 'Web CTMS',
            utm_medium: 'Empty List',
            utm_content: params.stage,
          },
        )}
        variant="neutral"
      >
        Find Loads on Loadboard
      </AnchorButton>
      <StyledText>or</StyledText>
      <Stack space="none" align="center">
        <TextBox color="secondary">
          Create or Import loads using the buttons
        </TextBox>
        <TextBox color="secondary">at the top right</TextBox>
      </Stack>
    </Stack>
  );
}

function LoadsPageEmptyResultWithSuggestedLoads() {
  return (
    <Stack space="large" align="center">
      <Stack space="medium" align="center">
        <EmptySearchLoadsIcon />
        <Stack space="small" align="center">
          <TextBox variant="heading-3">No New Loads</TextBox>
          <Box width="320px">
            <TextBox color="secondary" align="center">
              Create or Import loads using the buttons at the top right
            </TextBox>
          </Box>
        </Stack>
      </Stack>
      <Inline verticalAlign="center">
        <StyledDivider />
        <TextBox color="secondary">or</TextBox>
        <StyledDivider />
      </Inline>
      <ExploreSuggestedLoads source="Loads List" />
    </Stack>
  );
}

function LoadsPageEmptyResultContent() {
  const { params } = useLoadsPageContext();
  const { data = [] } = useLoadSuggestions([], {
    enabled: params.stage === 'new',
  });

  const shouldShowSuggestedLoadsNudging = useMemo(() => {
    const unseenSuggestedLoads = data.filter((load) => !load.is_seen);
    return params.stage === 'new' && unseenSuggestedLoads.length > 3;
  }, [data, params.stage]);

  if (shouldShowSuggestedLoadsNudging) {
    return <LoadsPageEmptyResultWithSuggestedLoads />;
  }

  return <LoadsPageEmptyResultFallback />;
}

export function LoadsPageEmptyResult() {
  const { isEmptyFilterParams, isEmptySearchParams } = useLoadsPageContext();
  const hasFilterOrSearch = !isEmptyFilterParams || !isEmptySearchParams;

  if (hasFilterOrSearch) {
    return <LoadsPageEmptyResultSearch />;
  }

  return <LoadsPageEmptyResultContent />;
}
